import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search, X, Loader } from 'lucide-react'
import { reportApi } from '../../../lib/api/reports'
import { EventTypeLookup } from '../../../types/reports'
import {
  MANDATORY_METRICS,
  DERIVED_METRICS,
  formatMetricLabel,
  isMandatoryMetric,
  isDerivedMetric,
} from '../metricsConfig'

interface Props {
  selectedMetrics: string[]
  onChange: (next: string[]) => void
}

interface DropdownPos {
  top: number
  left: number
}

const DEBOUNCE_MS = 300

export function MetricSelector({ selectedMetrics, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<EventTypeLookup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetchToken = useRef(0)

  const selectedOptional = selectedMetrics.filter(
    (m) => !isMandatoryMetric(m) && !isDerivedMetric(m)
  )

  // Fetch from server (debounced when searching, immediate when opening)
  const fetchEventTypes = useCallback(async (q: string) => {
    const token = ++fetchToken.current
    setIsLoading(true)
    try {
      const data = await reportApi.searchEventTypes(q, 50)
      if (token !== fetchToken.current) return
      setResults(data.filter((e) => !isMandatoryMetric(e.name)))
    } catch {
      if (token !== fetchToken.current) return
      setResults([])
    } finally {
      if (token === fetchToken.current) setIsLoading(false)
    }
  }, [])

  // Trigger search when search text changes (debounced)
  useEffect(() => {
    if (!open) return
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => fetchEventTypes(search), DEBOUNCE_MS)
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
  }, [search, open, fetchEventTypes])

  function computeDropdownPos(): DropdownPos | null {
    if (!buttonRef.current) return null
    const rect = buttonRef.current.getBoundingClientRect()
    return { top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX }
  }

  function openDropdown() {
    const pos = computeDropdownPos()
    if (!pos) return
    setDropdownPos(pos)
    setSearch('')
    setResults([])
    setOpen(true)
    // Immediate fetch (no debounce) on open
    fetchEventTypes('')
  }

  function closeDropdown() {
    setOpen(false)
    setSearch('')
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      closeDropdown()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  // Reposition on scroll / resize
  useEffect(() => {
    if (!open) return
    function reposition() {
      const pos = computeDropdownPos()
      if (pos) setDropdownPos(pos)
    }
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  function toggleOptional(name: string) {
    if (selectedMetrics.includes(name)) {
      onChange(selectedMetrics.filter((m) => m !== name))
    } else {
      onChange([...selectedMetrics, name])
    }
  }

  function toggleDerived(key: string) {
    if (selectedMetrics.includes(key)) {
      onChange(selectedMetrics.filter((m) => m !== key))
    } else {
      onChange([...selectedMetrics, key])
    }
  }

  function clearOptional() {
    onChange(selectedMetrics.filter((m) => isMandatoryMetric(m) || isDerivedMetric(m)))
  }

  const optionalCount = selectedOptional.length

  const dropdown = open && dropdownPos
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'absolute', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          className="w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
        >
          {/* Search */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <input
              autoFocus
              type="text"
              placeholder="Search event types..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-xs bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
            />
            {search ? (
              <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <X className="w-3 h-3" />
              </button>
            ) : isLoading ? (
              <Loader className="w-3 h-3 text-gray-400 animate-spin" />
            ) : null}
          </div>

          {/* Results */}
          <div className="max-h-52 overflow-y-auto py-1">
            {isLoading && results.length === 0 ? (
              <div className="flex items-center justify-center py-4 gap-2 text-xs text-gray-400">
                <Loader className="w-3.5 h-3.5 animate-spin" />
                Loading…
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">
                {search ? 'No matching event types' : 'No additional event types'}
              </div>
            ) : (
              results.map((et) => (
                <label
                  key={et.name}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedMetrics.includes(et.name)}
                    onChange={() => toggleOptional(et.name)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
                  />
                  <span className="text-xs text-gray-800 dark:text-gray-200 truncate">
                    {formatMetricLabel(et.name)}
                  </span>
                </label>
              ))
            )}
          </div>

          {/* Footer */}
          {optionalCount > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <span className="text-xs text-gray-400">{optionalCount} selected</span>
              <button
                onClick={() => { clearOptional(); closeDropdown() }}
                className="text-xs text-red-500 hover:text-red-600 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>,
        document.body
      )
    : null

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
        Metrics
      </span>

      <div className="flex flex-wrap items-center gap-1.5">
        {/* Mandatory — always on, locked */}
        {(MANDATORY_METRICS as readonly string[]).map((key) => (
          <span
            key={key}
            title="Always included"
            className="px-3 py-1 rounded-full text-xs font-medium border bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-600 cursor-default select-none"
          >
            ✓ {formatMetricLabel(key)}
          </span>
        ))}

        {/* Optional event types — dropdown trigger */}
        <>
          <button
            ref={buttonRef}
            onClick={open ? closeDropdown : openDropdown}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              optionalCount > 0
                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-600'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            {optionalCount > 0
              ? `✓ ${optionalCount} event type${optionalCount !== 1 ? 's' : ''}`
              : 'Event types'}
            {optionalCount > 0 ? (
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); clearOptional() }}
                className="hover:text-red-500 transition-colors"
                title="Clear selection"
              >
                <X className="w-3 h-3" />
              </span>
            ) : (
              <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
            )}
          </button>
          {dropdown}
        </>

        {/* Derived metrics separator + pills */}
        {DERIVED_METRICS.length > 0 && (
          <span className="text-gray-300 dark:text-gray-600 select-none">|</span>
        )}
        {DERIVED_METRICS.map(({ key, label }) => {
          const active = selectedMetrics.includes(key)
          return (
            <button
              key={key}
              onClick={() => toggleDerived(key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600'
                  : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              {active && <span className="mr-1">✓</span>}
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
